import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { vehiclesAPI, vinAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Trash2, Truck, Upload, X, Image as ImageIcon, Search } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function VehicleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [decodingVin, setDecodingVin] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    nickname: "",
    year: "",
    make: "",
    model: "",
    trim: "",
    vin: "",
    mileage_current: "",
    engine: "",
    transmission: "",
    transfer_case: "",
    front_axle: "",
    rear_axle: "",
    gear_ratio_front: "",
    gear_ratio_rear: "",
    tire_size: "",
    lift_height: "",
    fuel_capacity_gal: "",
    notes: "",
    primary_photo: "",
  });

  useEffect(() => {
    if (isEdit) {
      loadVehicle();
    }
  }, [id]);

  const handleDecodeVin = async () => {
    const vin = form.vin.trim().toUpperCase();
    if (vin.length !== 17) {
      toast.error("VIN must be 17 characters");
      return;
    }

    setDecodingVin(true);
    try {
      const { data } = await vinAPI.decode(vin);
      
      // Auto-fill fields from decoded VIN
      setForm(prev => ({
        ...prev,
        vin: data.vin || prev.vin,
        year: data.year || prev.year,
        make: data.make || prev.make,
        model: data.model || prev.model,
        trim: data.trim || prev.trim,
        engine: data.engine || prev.engine,
        transmission: data.transmission || prev.transmission,
      }));
      
      toast.success("VIN decoded! Fields auto-filled.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decode VIN");
    } finally {
      setDecodingVin(false);
    }
  };

  const loadVehicle = async () => {
    setLoading(true);
    try {
      const { data } = await vehiclesAPI.get(id);
      setForm({
        nickname: data.nickname || "",
        year: data.year || "",
        make: data.make || "",
        model: data.model || "",
        trim: data.trim || "",
        vin: data.vin || "",
        mileage_current: data.mileage_current || "",
        engine: data.engine || "",
        transmission: data.transmission || "",
        transfer_case: data.transfer_case || "",
        front_axle: data.front_axle || "",
        rear_axle: data.rear_axle || "",
        gear_ratio_front: data.gear_ratio_front || "",
        gear_ratio_rear: data.gear_ratio_rear || "",
        tire_size: data.tire_size || "",
        lift_height: data.lift_height || "",
        fuel_capacity_gal: data.fuel_capacity_gal || "",
        notes: data.notes || "",
        primary_photo: data.primary_photo || "",
      });
    } catch (error) {
      toast.error("Failed to load vehicle");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nickname.trim()) {
      toast.error("Nickname is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        mileage_current: form.mileage_current ? parseInt(form.mileage_current) : 0,
        fuel_capacity_gal: form.fuel_capacity_gal ? parseFloat(form.fuel_capacity_gal) : null,
      };

      if (isEdit) {
        await vehiclesAPI.update(id, payload);
        toast.success("Vehicle updated");
        navigate(`/vehicles/${id}`);
      } else {
        const { data } = await vehiclesAPI.create(payload);
        toast.success("Vehicle created");
        navigate(`/vehicles/${data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vehicle");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await vehiclesAPI.delete(id);
      toast.success("Vehicle deleted");
      navigate("/");
    } catch (error) {
      toast.error("Failed to delete vehicle");
    } finally {
      setDeleting(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      if (isEdit) {
        // Upload directly to vehicle
        const { data } = await vehiclesAPI.uploadPhoto(id, file);
        setForm((prev) => ({ ...prev, primary_photo: data.url }));
        toast.success("Photo uploaded");
      } else {
        // Convert to base64 for new vehicles
        const reader = new FileReader();
        reader.onload = (event) => {
          setForm((prev) => ({ ...prev, primary_photo: event.target.result }));
          toast.success("Photo added");
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removePhoto = () => {
    setForm((prev) => ({ ...prev, primary_photo: "" }));
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to={isEdit ? `/vehicles/${id}` : "/"}
              data-testid="back-btn"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <HeaderIcon />

              <h1 className="text-lg font-bold text-foreground">
                {isEdit ? "Edit Vehicle" : "Add Vehicle"}
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Photo Upload */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Vehicle Photo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-6">
                {/* Preview */}
                <div className="w-full sm:w-48 h-36 bg-zinc-900 rounded-sm border border-zinc-800 overflow-hidden flex items-center justify-center relative">
                  {form.primary_photo ? (
                    <>
                      <img
                        src={form.primary_photo}
                        alt="Vehicle"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500/80 hover:bg-red-500 rounded-sm flex items-center justify-center"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">No photo</p>
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <div className="flex-1 space-y-4">
                  <div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      data-testid="photo-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-photo-btn"
                      className="border-zinc-700 hover:bg-zinc-800"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload Photo
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      JPEG, PNG, WebP or GIF. Max 5MB.
                    </p>
                  </div>

                  {/* Or URL */}
                  <div className="space-y-2">
                    <Label htmlFor="primary_photo" className="text-xs text-muted-foreground">
                      Or enter image URL
                    </Label>
                    <Input
                      id="primary_photo"
                      data-testid="vehicle-photo-input"
                      value={form.primary_photo}
                      onChange={(e) => updateField("primary_photo", e.target.value)}
                      placeholder="https://..."
                      className="bg-zinc-900 border-zinc-800 text-sm"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="nickname">
                    Nickname <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="nickname"
                    data-testid="vehicle-nickname-input"
                    value={form.nickname}
                    onChange={(e) => updateField("nickname", e.target.value)}
                    placeholder="e.g., Trail Beast, Daily Driver"
                    className="bg-zinc-900 border-zinc-800"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year">Year</Label>
                  <Input
                    id="year"
                    data-testid="vehicle-year-input"
                    type="number"
                    value={form.year}
                    onChange={(e) => updateField("year", e.target.value)}
                    placeholder="2020"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="make">Make</Label>
                  <Input
                    id="make"
                    data-testid="vehicle-make-input"
                    value={form.make}
                    onChange={(e) => updateField("make", e.target.value)}
                    placeholder="Toyota, Jeep, Ford..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    data-testid="vehicle-model-input"
                    value={form.model}
                    onChange={(e) => updateField("model", e.target.value)}
                    placeholder="4Runner, Wrangler, Bronco..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trim">Trim</Label>
                  <Input
                    id="trim"
                    data-testid="vehicle-trim-input"
                    value={form.trim}
                    onChange={(e) => updateField("trim", e.target.value)}
                    placeholder="TRD Pro, Rubicon..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vin">VIN</Label>
                  <div className="flex gap-2">
                    <Input
                      id="vin"
                      data-testid="vehicle-vin-input"
                      value={form.vin}
                      onChange={(e) => updateField("vin", e.target.value.toUpperCase())}
                      onPaste={(e) => {
                        const pasted = e.clipboardData.getData("text").trim().toUpperCase();
                        if (pasted.length === 17) {
                          e.preventDefault();
                          updateField("vin", pasted);
                          // Auto-decode after a short delay to let state update
                          setTimeout(async () => {
                            setDecodingVin(true);
                            try {
                              const { data } = await vinAPI.decode(pasted);
                              setForm(prev => ({
                                ...prev,
                                vin: data.vin || prev.vin,
                                year: data.year || prev.year,
                                make: data.make || prev.make,
                                model: data.model || prev.model,
                                trim: data.trim || prev.trim,
                                engine: data.engine || prev.engine,
                                transmission: data.transmission || prev.transmission,
                              }));
                              toast.success("VIN pasted and decoded! Fields auto-filled.");
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to decode VIN");
                            } finally {
                              setDecodingVin(false);
                            }
                          }, 100);
                        }
                      }}
                      placeholder="Vehicle ID Number"
                      className="bg-zinc-900 border-zinc-800 font-mono uppercase flex-1"
                      maxLength={17}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDecodeVin}
                      disabled={decodingVin || form.vin.length !== 17}
                      data-testid="decode-vin-btn"
                      className="border-zinc-700 hover:bg-zinc-800"
                    >
                      {decodingVin ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter 17-character VIN and click search to auto-fill vehicle details
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mileage">Current Mileage</Label>
                  <Input
                    id="mileage"
                    data-testid="vehicle-mileage-input"
                    type="number"
                    value={form.mileage_current}
                    onChange={(e) => updateField("mileage_current", e.target.value)}
                    placeholder="0"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Drivetrain */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Drivetrain</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="engine">Engine</Label>
                  <Input
                    id="engine"
                    data-testid="vehicle-engine-input"
                    value={form.engine}
                    onChange={(e) => updateField("engine", e.target.value)}
                    placeholder="4.0L V6, 3.5L EcoBoost..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transmission">Transmission</Label>
                  <Input
                    id="transmission"
                    data-testid="vehicle-transmission-input"
                    value={form.transmission}
                    onChange={(e) => updateField("transmission", e.target.value)}
                    placeholder="6-Speed Manual, 10-Speed Auto..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transfer_case">Transfer Case</Label>
                  <Input
                    id="transfer_case"
                    data-testid="vehicle-transfer-case-input"
                    value={form.transfer_case}
                    onChange={(e) => updateField("transfer_case", e.target.value)}
                    placeholder="NP231, BW4406..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fuel_capacity">Fuel Capacity (gal)</Label>
                  <Input
                    id="fuel_capacity"
                    data-testid="vehicle-fuel-capacity-input"
                    type="number"
                    step="0.1"
                    value={form.fuel_capacity_gal}
                    onChange={(e) => updateField("fuel_capacity_gal", e.target.value)}
                    placeholder="23"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Axles & Gears */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Axles & Gears</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="front_axle">Front Axle</Label>
                  <Input
                    id="front_axle"
                    data-testid="vehicle-front-axle-input"
                    value={form.front_axle}
                    onChange={(e) => updateField("front_axle", e.target.value)}
                    placeholder="Dana 44, D60..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rear_axle">Rear Axle</Label>
                  <Input
                    id="rear_axle"
                    data-testid="vehicle-rear-axle-input"
                    value={form.rear_axle}
                    onChange={(e) => updateField("rear_axle", e.target.value)}
                    placeholder="Dana 44, 14 Bolt..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gear_ratio_front">Front Gear Ratio</Label>
                  <Input
                    id="gear_ratio_front"
                    data-testid="vehicle-gear-front-input"
                    value={form.gear_ratio_front}
                    onChange={(e) => updateField("gear_ratio_front", e.target.value)}
                    placeholder="4.88:1"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gear_ratio_rear">Rear Gear Ratio</Label>
                  <Input
                    id="gear_ratio_rear"
                    data-testid="vehicle-gear-rear-input"
                    value={form.gear_ratio_rear}
                    onChange={(e) => updateField("gear_ratio_rear", e.target.value)}
                    placeholder="4.88:1"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Suspension & Tires */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Suspension & Tires</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="lift_height">Lift Height</Label>
                  <Input
                    id="lift_height"
                    data-testid="vehicle-lift-input"
                    value={form.lift_height}
                    onChange={(e) => updateField("lift_height", e.target.value)}
                    placeholder="3 inch, Stock..."
                    className="bg-zinc-900 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tire_size">Tire Size</Label>
                  <Input
                    id="tire_size"
                    data-testid="vehicle-tire-input"
                    value={form.tire_size}
                    onChange={(e) => updateField("tire_size", e.target.value)}
                    placeholder="35x12.5R17"
                    className="bg-zinc-900 border-zinc-800 font-mono"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                id="notes"
                data-testid="vehicle-notes-input"
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Additional notes about this vehicle..."
                className="bg-zinc-900 border-zinc-800 min-h-[100px]"
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4 pt-4">
            {isEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="delete-vehicle-btn"
                    className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                    disabled={deleting}
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-zinc-950 border-zinc-800">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Vehicle?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this vehicle and all its
                      service records and mods. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-zinc-900 border-zinc-800">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      data-testid="confirm-delete-btn"
                      className="bg-red-500 hover:bg-red-600"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <div className="flex gap-3 ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(isEdit ? `/vehicles/${id}` : "/")}
                className="border-zinc-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="save-vehicle-btn"
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isEdit ? "Save Changes" : "Create Vehicle"}
              </Button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
